import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import {
  ArrowLeft,
  Check,
  ClipboardList,
  Clock,
  ExternalLink,
  Inbox,
  Loader2,
  Mail,
  ShieldCheck,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import { toast } from "sonner";

import {
  approveTrainFactoryListing,
  cancelTrainFactoryInvite,
  inviteTrainFactoryAdmin,
  listPendingTrainFactoryListings,
  listTrainFactoryAdmins,
  rejectTrainFactoryListing,
  revokeTrainFactoryAdmin,
} from "@/lib/train-factory-users.functions";
import { BS } from "@/components/admin/_shared";

const TF_ADMIN_ORIGIN = "https://factory.whatfinland.com";

function publicInviteLink(raw: string) {
  const token = /[?&]token=([^&#\s]+)/i.exec(raw || "")?.[1];
  return token ? `${TF_ADMIN_ORIGIN}/invite?token=${token}` : TF_ADMIN_ORIGIN;
}

type Tab = "invites" | "admins" | "listings";

export const Route = createFileRoute("/_authenticated/admin/train-factory-users")({
  component: TrainFactoryUsersPage,
  head: () => ({
    meta: [
      { title: "Train Factory users · What Finland admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

const INPUT_CLS =
  "mt-1 w-full rounded-md border border-[#ced4da] bg-white px-3 py-2 text-sm text-[#212529] shadow-sm transition-colors focus:border-[#86b7fe] focus:outline-none focus:ring-2 focus:ring-[#0d6efd]/25";

function SectionCard({
  icon: Icon,
  title,
  count,
  children,
}: {
  icon: typeof Users;
  title: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border bg-white shadow-sm" style={{ borderColor: BS.cardBorder }}>
      <div className="flex items-center gap-2.5 border-b px-5 py-3.5" style={{ borderColor: "#eef0f2" }}>
        <span
          className="grid size-7 shrink-0 place-items-center rounded-lg"
          style={{ background: "#e7f1ff", color: BS.primary }}
        >
          <Icon className="size-4" strokeWidth={2.1} />
        </span>
        <h2 className="text-[15px] font-semibold tracking-tight text-[#212529]">{title}</h2>
        {typeof count === "number" ? (
          <span
            className="ml-auto rounded-full px-2 py-0.5 text-[11px] font-semibold"
            style={{
              background: count > 0 ? "#fff3cd" : "#f1f3f5",
              color: count > 0 ? "#7a5c00" : BS.muted,
            }}
          >
            {count}
          </span>
        ) : null}
      </div>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

function EmptyState({ icon: Icon, text }: { icon: typeof Users; text: string }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-[#dee2e6] bg-[#f8f9fa] px-4 py-7 text-center">
      <Icon className="size-5 text-[#adb5bd]" strokeWidth={1.8} />
      <p className="max-w-xs text-[13px] text-[#6c757d]">{text}</p>
    </div>
  );
}

function StatTile({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof Users;
  label: string;
  value: number | "…";
  tone: "blue" | "amber" | "green";
}) {
  const tones = {
    blue: { bg: "#e7f1ff", fg: BS.primary },
    amber: { bg: "#fff8e1", fg: "#b26a00" },
    green: { bg: "#e6f4ea", fg: BS.success },
  }[tone];
  return (
    <div className="flex items-center gap-3 rounded-xl border bg-white px-4 py-3 shadow-sm" style={{ borderColor: BS.cardBorder }}>
      <span className="grid size-9 shrink-0 place-items-center rounded-lg" style={{ background: tones.bg, color: tones.fg }}>
        <Icon className="size-[18px]" strokeWidth={2} />
      </span>
      <div className="min-w-0">
        <div className="text-lg font-bold leading-tight text-[#212529]">{value}</div>
        <div className="truncate text-xs text-[#6c757d]">{label}</div>
      </div>
    </div>
  );
}

function DangerAction({ label, onClick, icon: Icon }: { label: string; onClick: () => void; icon?: typeof Trash2 }) {
  return (
    <button
      type="button"
      className="inline-flex shrink-0 items-center gap-1 rounded-md border border-[#f1aeb5] px-2.5 py-1 text-xs font-medium text-[#dc3545] transition-colors hover:bg-[#fdf0f1]"
      onClick={onClick}
    >
      {Icon ? <Icon className="size-3" /> : null}
      {label}
    </button>
  );
}

function TrainFactoryUsersPage() {
  const qc = useQueryClient();
  const listFn = useServerFn(listTrainFactoryAdmins);
  const inviteFn = useServerFn(inviteTrainFactoryAdmin);
  const revokeFn = useServerFn(revokeTrainFactoryAdmin);
  const cancelFn = useServerFn(cancelTrainFactoryInvite);
  const pendingListFn = useServerFn(listPendingTrainFactoryListings);
  const approveFn = useServerFn(approveTrainFactoryListing);
  const rejectFn = useServerFn(rejectTrainFactoryListing);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [lastLink, setLastLink] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("listings");

  const q = useQuery({
    queryKey: ["admin", "tf-users"],
    queryFn: () => listFn(),
    retry: false,
  });
  const pendingQ = useQuery({
    queryKey: ["admin", "tf-pending-listings"],
    queryFn: () => pendingListFn(),
    retry: false,
  });
  const forbidden = /forbidden/i.test(
    `${(q.error as Error | null)?.message ?? ""} ${(pendingQ.error as Error | null)?.message ?? ""}`,
  );

  const invite = useMutation({
    mutationFn: () => inviteFn({ data: { name: name.trim(), email: email.trim() } }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["admin", "tf-users"] });
      setLastLink(publicInviteLink(res.inviteUrl));
      setName("");
      setEmail("");
      setTab("invites");
      if (res.emailSent) toast.success(`Invite sent to ${res.email}`);
      else toast.message("User created. Copy the invite link below — email was not sent.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const revoke = useMutation({
    mutationFn: (userId: string) => revokeFn({ data: { userId } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "tf-users"] });
      toast.success("Train Factory access removed");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const approve = useMutation({
    mutationFn: (placeId: string) => approveFn({ data: { placeId } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "tf-pending-listings"] });
      qc.invalidateQueries({ queryKey: ["admin", "tf-pending-count"] });
      toast.success("Listing approved — it is now on the Train Factory page");
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const reject = useMutation({
    mutationFn: (placeId: string) => rejectFn({ data: { placeId } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "tf-pending-listings"] });
      qc.invalidateQueries({ queryKey: ["admin", "tf-pending-count"] });
      toast.success("Listing rejected");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const cancel = useMutation({
    mutationFn: (inviteId: string) => cancelFn({ data: { inviteId } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "tf-users"] });
      toast.success("Invite cancelled");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const admins = q.data?.users ?? [];
  const invites = q.data?.pendingInvites ?? [];
  const listings = pendingQ.data?.listings ?? [];

  if (forbidden) {
    return (
      <div className="mx-auto w-full max-w-lg px-4 py-16 text-center">
        <h1 className="text-lg font-semibold" style={{ color: BS.dark }}>
          Admin access required
        </h1>
        <p className="mt-2 text-sm" style={{ color: BS.muted }}>
          Your account cannot manage Train Factory users. Ask a site administrator for access.
        </p>
        <Link
          to="/admin/districts"
          className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#495057] hover:text-[#212529]"
        >
          <ArrowLeft className="size-4" />
          Back to Districts
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6">
      <div className="flex items-center justify-between gap-3">
        <Link
          to="/admin/districts"
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#495057] transition-colors hover:text-[#212529]"
        >
          <ArrowLeft className="size-4" />
          Districts
        </Link>
        <a
          href="https://whatfinland.com/train-factory"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 rounded-md border bg-white px-2.5 py-1.5 text-[13px] font-medium text-[#495057] shadow-sm transition-colors hover:bg-[#f8f9fa] hover:text-[#212529]"
          style={{ borderColor: BS.cardBorder }}
        >
          <ExternalLink className="size-3.5" />
          Preview page
        </a>
      </div>

      <header className="mt-3 mb-5">
        <div className="flex items-start gap-3">
          <span
            className="mt-0.5 grid size-10 shrink-0 place-items-center rounded-xl"
            style={{ background: "#e7f1ff", color: BS.primary }}
          >
            <ShieldCheck className="size-5" strokeWidth={2} />
          </span>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight text-[#212529]">Train Factory users</h1>
            <p className="mt-1 text-sm text-[#6c757d]">
              Enter a name and email. They are assigned to Train Factory and get an invite to set a password. No public
              signup, one Train Factory Admin role.
            </p>
          </div>
        </div>
      </header>

      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatTile icon={Users} label="Train Factory admins" value={q.isPending ? "…" : admins.length} tone="blue" />
        <StatTile icon={Mail} label="Pending invites" value={q.isPending ? "…" : invites.length} tone="amber" />
        <StatTile
          icon={ClipboardList}
          label="Listings awaiting approval"
          value={pendingQ.isPending ? "…" : listings.length}
          tone="green"
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <SectionCard icon={UserPlus} title="Create a Train Factory admin">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                invite.mutate();
              }}
            >
              <div className="grid gap-3">
                <label className="block text-sm font-medium text-[#212529]">
                  Name
                  <input
                    className={INPUT_CLS}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    maxLength={120}
                    placeholder="Full name"
                  />
                </label>
                <label className="block text-sm font-medium text-[#212529]">
                  Email
                  <input
                    type="email"
                    className={INPUT_CLS}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="name@trainfactory.fi"
                  />
                </label>
                <label className="block text-sm font-medium text-[#212529]">
                  Assigned to
                  <input
                    className="mt-1 w-full rounded-md border bg-[#f8f9fa] px-3 py-2 text-sm text-[#495057]"
                    style={{ borderColor: "#ced4da" }}
                    value="Train Factory only"
                    readOnly
                  />
                </label>
              </div>
              <button
                type="submit"
                disabled={invite.isPending}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:opacity-90 disabled:opacity-60 sm:w-auto"
                style={{ background: BS.primary }}
              >
                {invite.isPending ? <Loader2 className="size-4 animate-spin" /> : <UserPlus className="size-4" />}
                Create user and send invite
              </button>
              {lastLink ? (
                <div className="mt-4 rounded-lg border bg-[#f8f9fa] p-3" style={{ borderColor: "#e9ecef" }}>
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-[#212529]">
                    <Check className="size-3.5" style={{ color: BS.success }} />
                    Invite link — always on {TF_ADMIN_ORIGIN.replace("https://", "")}
                  </div>
                  <div className="mt-1.5 break-all rounded bg-white px-2 py-1.5 font-mono text-[11px] text-[#495057]" style={{ border: "1px solid #eef0f2" }}>
                    {lastLink}
                  </div>
                  <button
                    type="button"
                    className="mt-2 text-xs font-semibold"
                    style={{ color: BS.primary }}
                    onClick={() => {
                      void navigator.clipboard.writeText(lastLink);
                      toast.success("Copied");
                    }}
                  >
                    Copy link
                  </button>
                </div>
              ) : null}
            </form>
          </SectionCard>
        </div>

        <div className="lg:col-span-2">
          <SectionCard icon={ClipboardList} title="Listings waiting for approval" count={pendingQ.isPending ? undefined : listings.length}>
            {pendingQ.isPending ? (
              <div className="space-y-2.5">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="h-12 animate-pulse rounded-lg bg-[#f1f3f5]" />
                ))}
              </div>
            ) : listings.length === 0 ? (
              <EmptyState
                icon={Inbox}
                text="Nothing waiting. Train Factory staff submissions appear here for review."
              />
            ) : (
              <ul className="-mx-1 divide-y" style={{ borderColor: "#eef0f2" }}>
                {listings.map((item) => (
                  <li key={item.id} className="px-1 py-3">
                    <div className="truncate text-sm font-semibold text-[#212529]">{item.name}</div>
                    <div className="mt-0.5 truncate text-xs text-[#6c757d]">
                      {item.category || item.section}
                      {item.submitter ? ` · ${item.submitter}` : ""}
                      {item.address ? ` · ${item.address}` : ""}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
                        style={{ background: BS.success }}
                        onClick={() => approve.mutate(item.id)}
                      >
                        <Check className="size-3" />
                        Approve
                      </button>
                      <DangerAction
                        label="Reject"
                        onClick={() => {
                          if (!confirm(`Reject ${item.name}?`)) return;
                          reject.mutate(item.id);
                        }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </div>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2">
        <SectionCard icon={Clock} title="Pending invites" count={q.isPending ? undefined : invites.length}>
          {invites.length === 0 ? (
            <EmptyState icon={Clock} text="No unused invites right now." />
          ) : (
            <ul className="divide-y" style={{ borderColor: "#eef0f2" }}>
              {invites.map((inv) => (
                <li key={inv.id} className="flex items-center gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-[#212529]">{inv.name || inv.email}</div>
                    <div className="truncate text-xs text-[#6c757d]">
                      {inv.email} · expires{" "}
                      {inv.expires_at ? new Date(inv.expires_at).toLocaleDateString("en-GB") : "—"}
                    </div>
                  </div>
                  <DangerAction
                    label="Cancel"
                    onClick={() => {
                      if (!confirm(`Cancel invite for ${inv.email}?`)) return;
                      cancel.mutate(inv.id);
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard icon={Users} title="Train Factory admins" count={q.isPending ? undefined : admins.length}>
          {q.isPending ? (
            <div className="space-y-2.5">
              {[0, 1].map((i) => (
                <div key={i} className="h-12 animate-pulse rounded-lg bg-[#f1f3f5]" />
              ))}
            </div>
          ) : admins.length === 0 ? (
            <EmptyState icon={UserPlus} text="None yet. Create the first admin with the form above." />
          ) : (
            <ul className="divide-y" style={{ borderColor: "#eef0f2" }}>
              {admins.map((u) => (
                <li key={u.userId} className="flex items-center gap-3 py-3">
                  <span
                    className="grid size-8 shrink-0 place-items-center rounded-full text-xs font-bold text-white"
                    style={{ background: BS.primary }}
                  >
                    {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-[#212529]">{u.name || u.email}</div>
                    <div className="truncate text-xs text-[#6c757d]">
                      {u.email}
                      {u.lastSignInAt
                        ? ` · last sign-in ${new Date(u.lastSignInAt).toLocaleDateString("en-GB")}`
                        : " · has not signed in yet"}
                    </div>
                  </div>
                  <DangerAction
                    label="Remove"
                    icon={Trash2}
                    onClick={() => {
                      if (!confirm(`Remove Train Factory access for ${u.email}?`)) return;
                      revoke.mutate(u.userId);
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
